import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Pin, PinOff } from 'lucide-react';
import { useFlowSettingsStore, draftsFor, dirtyRoots } from '../../store/flowSettingsStore';
import { normalizeToBase } from '../../utils/symbolUtils';
import FlowEdgeSection from './FlowEdgeSection';
import AlertsSection from './AlertsSection';
import {
  FLOW_SETTINGS_ROOTS,
  buildPutBody,
  displayUnit,
  draftErrors,
  fmtValue,
  groupSchema,
  hasRootValue,
  isDirty,
  sameValue,
  sourceBadge,
  valueToInput,
  type FlowDraft,
  type FlowDrafts,
  type FlowLayer,
  type FlowParam,
} from './flowSettings';
import type { TradeFinderTabProps } from './tabs';

const DEFAULT_ROOT = 'MNQ';

/** Chart symbol's root if the selector offers it. */
export function settingsRootFor(symbol: string): string {
  const r = normalizeToBase(symbol);
  return (FLOW_SETTINGS_ROOTS as readonly string[]).includes(r) ? r : DEFAULT_ROOT;
}

/**
 * Root the tab opens on: the chart's, unless only another root has unsaved
 * edits (left there when the panel closed), so those are not hidden.
 */
export function initialSettingsRoot(chartRoot: string, drafts: Record<string, FlowDrafts>): string {
  const dirty = dirtyRoots(drafts);
  return dirty.length === 0 || dirty.includes(chartRoot) ? chartRoot : dirty[0];
}

/**
 * Root shown after discarding the current root's edits: another root that
 * still has unsaved edits (so they are not hidden behind the chart's root),
 * else the chart's.
 */
export function rootAfterDiscard(chartRoot: string, drafts: Record<string, FlowDrafts>): string {
  return initialSettingsRoot(chartRoot, drafts);
}

/** Dot on the Settings tab label while any root has unsaved edits. */
export const SettingsUnsavedBadge: React.FC = () => {
  const roots = useFlowSettingsStore((s) => dirtyRoots(s.drafts).join(','));
  if (!roots) return null;
  return (
    <span
      className="tradefinder-tab-dot"
      role="status"
      aria-label={`unsaved changes (${roots})`}
      title={`Unsaved settings changes: ${roots}`}
      data-testid="tradefinder-settings-unsaved"
    />
  );
};

interface RowProps {
  p: FlowParam;
  layer: FlowLayer | undefined;
  root: string;
  draft: FlowDraft | undefined;
  pinDraft: boolean | undefined;
  error: string | undefined;
  onDraft: (path: string, d: FlowDraft | null) => void;
  onPin: (path: string, pinned: boolean | null) => void;
}

/** One setting: Default | Tuned | Yours (editable) | Effective, source badge, pin. */
const ParamRow: React.FC<RowProps> = ({ p, layer, root, draft, pinDraft, error, onDraft, onPin }) => {
  const user = layer?.user ?? null;
  const rootValue = hasRootValue(layer, root);
  const id = `tf-set-${p.path.replace(/[^a-z0-9]/gi, '-')}`;
  const tid = `tradefinder-setting-${p.path}`;

  // What "Yours" shows: the draft, else your value.
  const shown = draft?.kind === 'set' ? draft.raw : draft?.kind === 'unset' ? null : user;

  /** A new raw value from the input: drop the draft when it equals your saved value. */
  const change = (raw: string | boolean) => {
    if (raw === '') {
      onDraft(p.path, rootValue ? { kind: 'unset' } : null);
      return;
    }
    onDraft(p.path, sameValue(raw, user) && user !== null ? null : { kind: 'set', raw });
  };

  let input: React.ReactNode;
  if (p.type === 'bool') {
    const on = Boolean(shown ?? layer?.effective ?? p.default);
    input = (
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={`${p.label}: yours`}
        className={`tf-set-switch ${on ? 'is-on' : ''} ${shown === null ? 'is-inherited' : ''}`}
        onClick={() => change(!on)}
        data-testid={`${tid}-input`}
      >
        <span className="levels-switch" aria-hidden="true"><span className="levels-switch-knob" /></span>
      </button>
    );
  } else if (p.type === 'enum') {
    input = (
      <select
        id={id}
        className="tf-set-input"
        aria-label={`${p.label}: yours`}
        value={valueToInput(shown)}
        onChange={(e) => change(e.target.value)}
        data-testid={`${tid}-input`}
      >
        <option value="">—</option>
        {(p.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  } else {
    input = (
      <input
        id={id}
        className={`tf-set-input ${error ? 'is-invalid' : ''}`}
        type="number"
        inputMode={p.type === 'integer' ? 'numeric' : 'decimal'}
        aria-label={`${p.label}: yours`}
        aria-invalid={!!error}
        min={p.min ?? undefined}
        max={p.max ?? undefined}
        step={p.step ?? (p.type === 'integer' ? 1 : 'any')}
        placeholder="—"
        value={valueToInput(shown)}
        onChange={(e) => change(e.target.value)}
        data-testid={`${tid}-input`}
      />
    );
  }

  const badge = sourceBadge(layer?.source, root);
  const pinned = pinDraft ?? layer?.pinned ?? false;
  const tuned = layer?.autotune ?? null;
  const changed = draft !== undefined || pinDraft !== undefined;

  return (
    <div className={`tf-set-row ${changed ? 'is-dirty' : ''} ${error ? 'has-error' : ''}`} data-testid={tid}>
      <div className="tf-set-row-head">
        <label className="tf-set-label" htmlFor={id} title={p.help}>
          {p.label}
          {displayUnit(p) && <span className="tf-set-unit" data-testid={`${tid}-unit`}>{displayUnit(p)}</span>}
        </label>
        {rootValue && draft?.kind !== 'unset' && (
          <button
            type="button"
            className="tf-set-link"
            onClick={() => onDraft(p.path, { kind: 'unset' })}
            title="Remove your value for this root"
            data-testid={`${tid}-unset`}
          >
            use {p.tunable && tuned !== null ? 'tuned' : 'default'}
          </button>
        )}
        {changed && (
          <button
            type="button"
            className="tf-set-link"
            onClick={() => { onDraft(p.path, null); onPin(p.path, null); }}
            data-testid={`${tid}-undo`}
          >
            undo
          </button>
        )}
        {p.tunable && (
          <button
            type="button"
            className={`tf-set-pin ${pinned ? 'is-pinned' : ''}`}
            aria-pressed={pinned}
            aria-label={`Pin ${p.label}`}
            title={pinned ? "pinned: tuner won't change this" : 'not pinned: the nightly tuner may change this'}
            onClick={() => {
              const next = !pinned;
              onPin(p.path, next === (layer?.pinned ?? false) ? null : next);
            }}
            data-testid={`${tid}-pin`}
          >
            {pinned ? <Pin size={12} /> : <PinOff size={12} />}
          </button>
        )}
      </div>
      <div className="tf-set-cols">
        <span className="tf-set-cell" data-testid={`${tid}-default`}>{fmtValue(layer?.default ?? p.default)}</span>
        <span className="tf-set-cell" data-testid={`${tid}-tuned`}>{fmtValue(tuned)}</span>
        <span className="tf-set-cell tf-set-yours">{input}</span>
        <span className="tf-set-cell tf-set-effective" data-testid={`${tid}-effective`}>
          {fmtValue(layer?.effective)}
          <span className={`tf-set-badge is-${badge}`} data-testid={`${tid}-source`} title={layer?.source}>{badge}</span>
        </span>
      </div>
      {draft?.kind === 'unset' && <div className="tf-set-hint">your value will be removed</div>}
      {error && <div className="tf-set-error" role="alert" data-testid={`${tid}-error`}>{error}</div>}
    </div>
  );
};

/**
 * flow-signals thresholds for one root (default: the chart's), layer by
 * layer, editable, saved in one PUT that the service hot-reloads; plus the
 * self-tuner's per-kind edge.
 */
const SettingsTab: React.FC<TradeFinderTabProps> = ({ symbol }) => {
  const chartRoot = settingsRootFor(symbol);
  const [root, setRoot] = useState(() => initialSettingsRoot(chartRoot, useFlowSettingsStore.getState().drafts));
  const schema = useFlowSettingsStore((s) => s.schema);
  const schemaError = useFlowSettingsStore((s) => s.schemaError);
  const rootState = useFlowSettingsStore((s) => s.byRoot[root]);
  const fetchSchema = useFlowSettingsStore((s) => s.fetchSchema);
  const fetchSettings = useFlowSettingsStore((s) => s.fetchSettings);
  const fetchTune = useFlowSettingsStore((s) => s.fetchTune);
  const save = useFlowSettingsStore((s) => s.save);
  // Drafts live in the store (per root): they outlive this tab and the panel.
  const drafts = useFlowSettingsStore((s) => draftsFor(s.drafts, root));
  const setDraft = useFlowSettingsStore((s) => s.setDraft);
  const setPinDraft = useFlowSettingsStore((s) => s.setPinDraft);
  const clearDrafts = useFlowSettingsStore((s) => s.clearDrafts);

  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [applied, setApplied] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [openOverride, setOpenOverride] = useState<Record<string, boolean>>({});

  const dirty = isDirty(drafts);

  // Follow the chart's root while nothing is pending.
  useEffect(() => {
    if (!dirty) setRoot(chartRoot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRoot]);

  useEffect(() => {
    void fetchSchema();
    void fetchTune();
  }, [fetchSchema, fetchTune]);

  useEffect(() => {
    void fetchSettings(root);
    setServerErrors({});
    setBanner(null);
    setApplied(null);
  }, [root, fetchSettings]);

  const groups = useMemo(() => groupSchema(schema ?? []), [schema]);
  const localErrors = useMemo(() => draftErrors(drafts, schema ?? []), [drafts, schema]);
  const invalid = Object.keys(localErrors).length > 0;
  const layers = rootState?.data?.layers ?? {};

  const touch = useCallback((path: string) => {
    setApplied(null);
    setBanner(null);
    setServerErrors((e) => {
      if (!(path in e)) return e;
      const next = { ...e };
      delete next[path];
      return next;
    });
  }, []);

  const onDraft = useCallback((path: string, d: FlowDraft | null) => {
    touch(path);
    setDraft(root, path, d);
  }, [touch, setDraft, root]);

  const onPin = useCallback((path: string, pinned: boolean | null) => {
    touch(path);
    setPinDraft(root, path, pinned);
  }, [touch, setPinDraft, root]);

  const onSave = async () => {
    if (!schema || !dirty || invalid || saving) return;
    let body;
    try {
      body = buildPutBody(drafts, schema);
    } catch (e) {
      setBanner(e instanceof Error ? e.message : String(e));
      return;
    }
    setSaving(true);
    setBanner(null);
    const r = await save(root, body);
    if (r.ok) {
      clearDrafts(root);
      setServerErrors({});
      await fetchSettings(root);
      setApplied(
        r.data?.written === false
          ? 'Nothing changed: the file already had these values.'
          : `Applied live to ${root}. Its rolling state restarted (MVI and stop-volume lines from 0, open detectors reset).`,
      );
    } else if (r.path) {
      setServerErrors({ [r.path]: r.error });
      const g = groups.find((x) => x.params.some((p) => p.path === r.path));
      if (g) setOpenOverride((o) => ({ ...o, [g.key]: true }));
      if (!schema.some((p) => p.path === r.path)) setBanner(r.error);
    } else {
      setBanner(r.error);
    }
    setSaving(false);
  };

  // Discard is per root: it drops this root's edits only. Another root's
  // unsaved edits (left from another chart, or before the panel closed) are
  // never thrown away unseen: the tab moves to that root next, so the dot on
  // the tab label always points at something on screen.
  const onDiscard = () => {
    clearDrafts(root);
    setServerErrors({});
    setBanner(null);
    setRoot(rootAfterDiscard(chartRoot, useFlowSettingsStore.getState().drafts));
  };

  const otherDirty = useFlowSettingsStore((s) => dirtyRoots(s.drafts).filter((r) => r !== root).join(','));

  const loadError = schemaError ?? rootState?.error ?? null;

  return (
    <div className="tf-settings" data-testid="tradefinder-settings-tab">
      <AlertsSection />
      <div className="tf-settings-top">
        <label className="tf-settings-root">
          <span>Root</span>
          <select
            value={root}
            onChange={(e) => setRoot(e.target.value)}
            disabled={dirty}
            title={dirty ? 'Save or discard your changes first' : 'Instrument whose settings you edit'}
            data-testid="tradefinder-settings-root"
          >
            {FLOW_SETTINGS_ROOTS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        {otherDirty && (
          <span className="tf-settings-muted tf-settings-other-dirty" data-testid="tradefinder-settings-other-dirty">
            also unsaved: {otherDirty}
          </span>
        )}
        {rootState?.loading && <span className="tf-settings-muted">loading…</span>}
        {rootState?.data && (
          <span className="tf-settings-muted" title={rootState.data.autotune ?? undefined}>
            tuner file: {rootState.data.autotune ? 'loaded' : 'none yet'}
          </span>
        )}
      </div>

      {loadError && (
        <div className="tf-settings-banner is-error" role="alert" data-testid="tradefinder-settings-load-error">{loadError}</div>
      )}
      {applied && (
        <div className="tf-settings-banner is-ok" role="status" data-testid="tradefinder-settings-applied">{applied}</div>
      )}

      {schema && groups.map((g) => {
        const open = openOverride[g.key] ?? g.defaultOpen;
        const mine = g.params.filter((p) => hasRootValue(layers[p.path], root)).length;
        const pending = g.params.filter((p) => drafts.values[p.path] || drafts.pins[p.path] !== undefined).length;
        return (
          <div className="tf-set-group" key={g.key} data-testid={`tradefinder-settings-group-${g.key}`}>
            <button
              type="button"
              className="tf-set-group-head"
              aria-expanded={open}
              onClick={() => setOpenOverride((o) => ({ ...o, [g.key]: !open }))}
              data-testid={`tradefinder-settings-group-toggle-${g.key}`}
            >
              {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              <span className="tf-set-group-name">{g.group}</span>
              <span className="tf-set-group-count">{g.params.length}</span>
              {mine > 0 && <span className="tf-set-group-tag">{mine} yours</span>}
              {pending > 0 && <span className="tf-set-group-tag is-pending">{pending} unsaved</span>}
            </button>
            {open && (
              <>
                <div className="tf-set-cols tf-set-cols-head" aria-hidden="true">
                  <span>Default</span><span>Tuned</span><span>Yours</span><span>Effective</span>
                </div>
                {g.params.map((p) => (
                  <ParamRow
                    key={p.path}
                    p={p}
                    layer={layers[p.path]}
                    root={root}
                    draft={drafts.values[p.path]}
                    pinDraft={drafts.pins[p.path]}
                    error={serverErrors[p.path] ?? localErrors[p.path]}
                    onDraft={onDraft}
                    onPin={onPin}
                  />
                ))}
              </>
            )}
          </div>
        );
      })}

      <FlowEdgeSection root={root} />

      {(dirty || banner) && (
        <div className="tf-settings-footer" data-testid="tradefinder-settings-footer">
          {banner && <div className="tf-settings-banner is-error" role="alert" data-testid="tradefinder-settings-error">{banner}</div>}
          {dirty && (
            <div className="tf-settings-actions">
              <span className="tf-settings-muted">
                {invalid ? 'Fix the highlighted values' : `Saving hot-reloads ${root}: its MVI / stop-volume lines restart`}
              </span>
              <button type="button" className="tf-settings-btn" onClick={onDiscard} disabled={saving} data-testid="tradefinder-settings-discard">
                Discard
              </button>
              <button
                type="button"
                className="tf-settings-btn is-primary"
                onClick={() => void onSave()}
                disabled={saving || invalid}
                data-testid="tradefinder-settings-save"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SettingsTab;
